<template>
    <TicketComponent
    v-if="isTicketEnabled"
    :show="showTicket && !hideTicket"
    :order="order"
    :order-display-text="getOrderDisplayText(order)"
    :hide-code="isRbeOrderButton"
    :rbe-open="rbeTicketOpen"
    mode="hierarchical"
    @close="showTicket = false"
  />
  <div v-if="!isTicketEnabled && !isRbeOrderButton" class="order-id" data-testid="selected-order-code" translate="no">{{ getOrderDisplayText(order) }}</div>
  <div class="order-info-wrapper" :class="{ 'order-info-wrapper--rbe': isRbeOrderButton }">
    <div v-if="order" class="order-info">
      <button
        class="back-button"
        data-testid="order-back"
        @click="$emit('clear-order', 'back_button')"
      >
        <template v-if="systemLane"><BzIcon name="arrow_back" class="action-icon" />{{ $t('message.back') }}</template>
        <template v-else><i class="pi pi-arrow-left"></i>&nbsp;{{ $t('message.back') }}</template>
      </button>
      
      <button
        class="camera-button"
        data-testid="camera-shutter"
        :class="{
          'camera-button--cooldown': captureCooldown,
          'camera-button--bounce': showBounce
        }"
        :disabled="captureCooldown"
        :aria-label="$t('message.takePhoto')"
        @click="handleCameraClick"
      >
        <BzIcon v-if="systemLane" name="photo_camera" class="action-icon" />
        <i v-else class="pi pi-camera"></i>
        <!-- Always mounted, never v-if: mounting the SVG inside the tap itself cost a layout and
             a raster at the worst moment, and the first frame always arrived late. Only a class
             changes now. -->
        <svg class="cooldown-ring" :class="{ 'cooldown-ring--running': cooldownRunning }" viewBox="0 0 96 96" aria-hidden="true">
          <rect class="cooldown-ring__track" x="4" y="4" width="88" height="88" :rx="ringRadius"
            fill="none" stroke-width="3" />
          <path class="cooldown-ring__progress"
            :d="ringPath"
            fill="none" stroke-width="3" stroke-linecap="round"
            pathLength="100" />
        </svg>
      </button>
      
      <!-- One tap for the photo and its analysis, until the order has a photo of this visit (the system's
           lane, CameraScreen's `photoAndAnalyze`). The shutter beside it still takes photos one by one. -->
      <button
        v-if="restaurant.proActivated && photoAndAnalyze"
        class="analyze-button analyze-button--shoot"
        data-testid="photo-and-analyze"
        :disabled="captureCooldown"
        @click.stop="$emit('photo-and-analyze')"
      >
        <span>{{ $t('message.photoAndAnalyze') }}</span>
      </button>
      <button
        v-else-if="restaurant.proActivated && photoCount > 0"
        class="analyze-button"
        :class="{ 'analyze-button--invite': inviteAnalyze }"
        data-testid="analyze-order"
        @click.stop="$emit('analyze-order', $event)"
      >
        <div v-if="photoCount > 0" class="photo-count">{{ photoCount }}</div>
        <span>{{ $t('message.analyze') }}</span>
      </button>
      <button
        v-if="!restaurant.proActivated"
        class="analyze-button"
        data-testid="analyze-order"
        @click.stop="$emit('clear-order', 'finalize')"
      >
        <div v-if="photoCount > 0" class="photo-count">{{ photoCount }}</div>
        <span>{{ $t('message.finalize') }}</span>
      </button>
    </div>
  </div>
</template>

<script>
import TicketComponent from './TicketComponent.vue';
import BzIcon from '../ui/components/BzIcon.vue';
import posthog from 'posthog-js';
import { systemLane } from '../ui/lane';
import { liveDemoOn } from '@/liveDemo/flag';

/**
 * The cooldown ring's outline, starting at the middle of the top edge: a rounded square of the viewBox
 * (88 at 4,4) whose corner follows the button's. Production's button has a radius of 16; the design
 * system's 96 button has 20, and the ring follows it (docs/sistema-diseno/forma-y-tamano.md).
 */
const ringPathFor = (r) => `M 48 4 L ${92 - r} 4 A ${r} ${r} 0 0 1 92 ${4 + r} L 92 ${92 - r} A ${r} ${r} 0 0 1 ${92 - r} 92 `
  + `L ${4 + r} 92 A ${r} ${r} 0 0 1 4 ${92 - r} L 4 ${4 + r} A ${r} ${r} 0 0 1 ${4 + r} 4 Z`;

export default {
  name: 'SelectedOrderInfoComponent',
  components: {
    TicketComponent,
    BzIcon
  },
  emits: ['clear-order', 'analyze-order', 'camera-click', 'photo-and-analyze'],
  data() {
    return {
      showTicket: true,
      // Live sales demo on: no PostHog flag to turn the ticket on, and the ticket is what the demo shows.
      isTicketEnabled: liveDemoOn() || posthog.isFeatureEnabled('ticket'),
      isRbeOrderButton: posthog.isFeatureEnabled('rbe-order-button'),
      showBounce: false,
      // The service screen this page was painted with (src/ui/lane.js): fixed for the page's life.
      systemLane: systemLane()
    }
  },
  computed: {
    ringRadius() {
      return this.systemLane ? 20 : 16;
    },
    ringPath() {
      return ringPathFor(this.ringRadius);
    }
  },
  props: {
    order: {
      type: Object,
      required: true
    },
    photoCount: {
      type: Number,
      default: 0
    },
    restaurant: {
      type: Object,
      required: true
    },
    captureCooldown: {
      type: Boolean,
      default: false
    },
    // The countdown, which starts when the photo lands — not when the button locks. In
    // imageCapture mode the sensor takes ~1.35s, so the two are more than a second apart.
    cooldownRunning: {
      type: Boolean,
      default: false
    },
    rbeTicketOpen: {
      type: Boolean,
      default: false
    },
    // «Foto y analizar» in Analizar's place: no photo of this visit yet (CameraScreen decides).
    photoAndAnalyze: {
      type: Boolean,
      default: false
    },
    // The first photo came from the shutter: Analizar lights up once to say it is the next step.
    inviteAnalyze: {
      type: Boolean,
      default: false
    },
    // The ticket steps aside while the list of what to photograph again holds the top (a phone).
    hideTicket: {
      type: Boolean,
      default: false
    }
  },
  watch: {
    cooldownRunning(newVal, oldVal) {
      if (oldVal && !newVal) {
        this.showBounce = true;
        setTimeout(() => { this.showBounce = false; }, 300);
      }
    }
  },
  methods: {
    getOrderDisplayText(order) {
      if (order.ticketId) {
        return order.ticketId;
      } else if (order.deliveryId) {
        return order.deliveryId;
      } else {
        return order.posId || this.$t('message.unknown');
      }
    },
    handleCameraClick() {
      // this.showTicket = false;
      this.$emit('camera-click');
    }
  }
};
</script>

<style scoped>
.order-info-wrapper {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 156px;
  padding-bottom: 16px;  
}

.order-info {
  height: 100%;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  padding: 0 20px;
}

.back-button {
  justify-self: start;
}

.camera-button {
  justify-self: center;
  grid-column: 2;
}

.analyze-button {
  justify-self: end;
}

.back-button, .camera-button, .analyze-button {
  height: 88px;
  border-radius: 16px;
  box-shadow: 0px 0px 16px 0px var(--bz-p-0c1117-a30);
  font-family: "DM Sans";
  font-size: 28px;
  font-weight: 700;
  cursor: pointer;
  user-select: none;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 32px;
}

.back-button {
  background: linear-gradient(
        var(--bz-p-9b9b9b-a90),
        var(--bz-p-9b9b9b-a90)
      )
      padding-box,
    linear-gradient(to bottom right, var(--bz-p-white), var(--bz-p-626262)) border-box;
  border: 1px solid transparent;
  color: var(--bz-p-0c1117);
}

.camera-button {
  background: var(--bz-p-blue-600);
  border: none;
  color: var(--bz-p-white);
  width: 88px;
  padding: 0;
  position: relative;
  overflow: visible;
}

.camera-button .pi-camera {
  font-size: 32px;
}

.analyze-button {
  background: linear-gradient(var(--bz-p-3dd668), var(--bz-p-3dd668)) padding-box,
    linear-gradient(to bottom right, var(--bz-p-3dd668), var(--bz-p-a7f1bc)) border-box;
  border: 1px solid transparent;
  color: var(--bz-p-0c1117);
  position: relative;
  flex-direction: column;
  padding-top: 20px;
  padding-bottom: 10px;
}

.photo-count {
  background-color: var(--bz-p-9be6a1);
  border: 1px solid var(--bz-p-28aa4d);
  border-radius: 50px;
  padding: 5px 10px;
  font-size: 14px;
  min-width: 24px;
  height: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top:-15px;
}

.analyze-button span {
  margin-top: 5px;
}

.order-id {
  border-radius: 16px;
  border: 1px solid var(--bz-p-edf0f3);
  background: var(--bz-p-edf0f3-a50);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--bz-p-0c1117);
  font-family: "DM Sans";
  font-weight: 700;
  position: absolute;
  padding:10px;
  
  /* Desktop/Tablet styles */
  min-width: 184px;
  height: 100px;
  font-size: 40px;
  top: 70px;
  right: 90px;
}

/* Mobile styles */
@media (max-width: 767px) {
  .order-id {
    min-width: 114px;
    height: 63px;
    font-size: 30px;
    top: 70px;
    left: 50%;
    transform: translateX(-50%);
  }

  .order-info {
    padding: 0 10px;
  }

  /* RBE pilot: controls become an in-flow bottom strip (not overlaying the camera) so the viewfinder
     stays clean — closer to a native camera app. */
  .order-info-wrapper--rbe {
    position: static;
    box-sizing: border-box;
    height: auto;
    /* 18px = app-standard side margin. border-box keeps width:100% + padding from overflowing (which
       was pushing the whole row right: analyze glued to the edge, camera button off-center). */
    padding: 10px 18px 16px;
    background-color: var(--bz-p-0c1117);
    flex-shrink: 0;
  }
  /* The wrapper owns the 18px side margin; neutralize the inner grid's own padding so the back / analyze
     buttons land exactly on 18px and the camera button stays centered. */
  .order-info-wrapper--rbe .order-info {
    padding: 0;
  }

  .back-button, .camera-button, .analyze-button {
    width: 80px;
    height: 80px;
    font-size: 18px;
    padding: 0;
  }

  .back-button, .camera-button {
    flex-direction: column;
  }

  .back-button i {
    font-size: 24px;
    margin-bottom: 5px;
  }

  .camera-button i {
    font-size: 22px;
    margin-bottom: 0;
  }

  .analyze-button {
    padding-top: 15px;
    padding-bottom: 5px;
  }

  .photo-count {
    top: -8px;
    font-size: 12px;
    min-width: 20px;
    height: 20px;
  }

  .analyze-button span {
    font-size: 16px;
    margin-top: 3px;
  }
}

/* Cooldown states */
.camera-button--cooldown {
  background: var(--bz-p-blue-600-a55);
  pointer-events: none;
}

.camera-button--cooldown .pi-camera {
  opacity: 0.55;
}

/* The bounce that says «ready again» moves the button: only for those who have not asked the system for
   less motion (docs/sistema-diseno/forma-y-tamano.md, step 1). Without it, the ring ending says the same. */
@media (prefers-reduced-motion: no-preference) {
  .camera-button--bounce {
    animation: cameraBounce 0.3s ease-out;
  }
}

@keyframes cameraBounce {
  0% { transform: scale(1); }
  40% { transform: scale(1.08); }
  100% { transform: scale(1); }
}

/* Ring progress overlay.
   ------------------------------------------------------------------------------------------
   One SVG path, traced with stroke-dashoffset, linear. That is the simple version, and the
   history of this ring is the history of not needing anything cleverer:

   - Originally `linear`, which was smooth but cost the main thread: `stroke-dashoffset` is not
     compositable, so each of its ~150 ticks is a style recalc plus an SVG repaint — and the ring
     started ON THE SHUTTER TAP, so all of that landed on top of the capture. Measured on a Honor
     Pad X9A: 206.8ms of dropped-frame time per photo.
   - So it became `steps(25)`, which cut the cost to the same as deleting the ring — by running at
     10fps. Operators read that, correctly, as jerky.
   - Then it was rebuilt out of nine composited HTML elements. Immune to the main thread, but the
     four corners cannot be drawn with a transform, so they faded in: four visible jumps per lap.
     Worse than what it replaced.

   What actually removed the problem was elsewhere: the ring now starts when the PHOTO LANDS, not
   at the tap (see _startCooldownWindow). Nothing expensive is running during those 2.5s — the
   capture is over, the encode is in a worker — so the main thread can afford to repaint a path,
   and the animation is continuous the whole way round, corners included.

   If the ring is ever moved back to overlapping the capture, this becomes a problem again. */
.cooldown-ring {
  position: absolute;
  top: -4px;
  left: -4px;
  width: calc(100% + 8px);
  height: calc(100% + 8px);
  pointer-events: none;
  z-index: 1;
  /* Hidden at rest rather than unmounted. Keeping it in the DOM is what stops the tap from paying
     for a layout and a raster, but it must show nothing between captures — and a hidden dashed
     stroke is not nothing: with `stroke-linecap: round`, a dash of zero visible length still
     paints a round cap, i.e. a white dot sitting permanently at the path's start point (the
     middle of the top edge). The v-if used to hide that by not existing. */
  opacity: 0;
}

.cooldown-ring--running {
  opacity: 1;
}

/* The ring's colours, moved here from the SVG attributes (stage 2 of the Bronze UI migration
   left them there until the worker branch, which rewrote these lines, had landed). A
   presentation attribute loses to any rule, so the same value here paints the same ring. */
.cooldown-ring__track {
  stroke: var(--bz-p-white-a10);
}

.cooldown-ring__progress {
  stroke: var(--bz-p-white);
  stroke-dasharray: 100;
  stroke-dashoffset: 100;
}

.cooldown-ring--running .cooldown-ring__progress {
  animation: ringFill 2.5s linear forwards;
}

@keyframes ringFill {
  to { stroke-dashoffset: 0; }
}
/* ════ The open order on the design system (src/ui/lane.js: `ui-system-lane`, per restaurant) ════
   docs/sistema-diseno/forma-y-tamano.md, «Pedido abierto»: the three actions at 96 (`--bz-target-hero`)
   with the radius of a 96 button and a 22/600 label, flat; Material icons at 32; the photo count a 32 pill
   beside its label; the order's code on a solid plate of what floats (no blur over the live camera); the
   gutter on the three sides. Production's rules above stay for the stores without the flag. */
:root[data-bz-lane="system"] .order-info-wrapper {
  box-sizing: border-box;
  height: auto;
  padding: 0 0 var(--bz-gutter);
}

:root[data-bz-lane="system"] .order-info {
  gap: var(--bz-target-gap);
  padding: 0 var(--bz-gutter);
}

:root[data-bz-lane="system"] .back-button,
:root[data-bz-lane="system"] .camera-button,
:root[data-bz-lane="system"] .analyze-button {
  box-sizing: border-box;
  height: var(--bz-target-hero);
  gap: var(--bz-space-3);
  padding: 0 var(--bz-space-7);
  border: 1px solid transparent;
  border-radius: var(--bz-radius-xl);
  box-shadow: none;
  font-family: var(--bz-font);
  font-size: var(--bz-text-xl);
  line-height: var(--bz-leading-xl);
  font-weight: var(--bz-weight-label);
}

:root[data-bz-lane="system"] .action-icon {
  font-size: var(--bz-icon-lg);
}

:root[data-bz-lane="system"] .back-button {
  padding-left: var(--bz-space-6);
  border-color: var(--bz-edge);
  background: var(--bz-control);
  color: var(--bz-ink);
}

:root[data-bz-lane="system"] .camera-button {
  width: var(--bz-target-hero);
  padding: 0;
  background: var(--bz-action);
  color: var(--bz-on-action);
}

/* Cooling down: the selection's dark blue, the icon in the blue that reads on it — still the shutter,
   not yet ready. The ring, inside the button, keeps its full colour. */
:root[data-bz-lane="system"] .camera-button--cooldown {
  background: var(--bz-action-container);
  color: var(--bz-action-ink);
}

:root[data-bz-lane="system"] .analyze-button {
  flex-direction: row;
  padding: 0 var(--bz-space-6) 0 var(--bz-space-7);
  background: var(--bz-ok-solid);
  color: var(--bz-on-ok-solid);
}

:root[data-bz-lane="system"] .analyze-button span {
  margin: 0;
}

:root[data-bz-lane="system"] .analyze-button .photo-count {
  order: 2;
  box-sizing: border-box;
  min-width: var(--bz-space-7);
  height: var(--bz-space-7);
  margin: 0;
  padding: 0 var(--bz-space-2);
  border: 0;
  border-radius: var(--bz-radius-full);
  background: var(--bz-ok-container);
  color: var(--bz-on-ok-container);
  font-size: var(--bz-text-sm);
  line-height: var(--bz-leading-sm);
  font-weight: var(--bz-weight-label);
}

/* «Foto y analizar»: Analizar's place and colour, its words only — the shutter beside it is the camera. */
:root[data-bz-lane="system"] .analyze-button--shoot {
  padding: 0 var(--bz-space-7);
  text-align: center;
}

/* The first photo came from the shutter: Analizar is the next step, and says so once. A ring in its own
   green opens out from it and fades, three times, and stops (nothing loops, norm 1). Its opacity and
   transform are all that change, on a layer of its own: the compositor draws it, and the main thread —
   busy with the photo that just landed and with the shutter's ring — paints the ring once
   (docs/sistema-diseno/forma-y-tamano.md, «Pedido abierto»). Without motion, the ring only fades in and
   out, in place. */
:root[data-bz-lane="system"] .analyze-button--invite::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  box-shadow: 0 0 0 8px var(--bz-ok-solid);
  opacity: 0;
  pointer-events: none;
}

@media (prefers-reduced-motion: no-preference) {
  :root[data-bz-lane="system"] .analyze-button--invite::after {
    animation: analyzeInvite 1.2s var(--bz-ease-out) 3;
  }
}

@media (prefers-reduced-motion: reduce) {
  :root[data-bz-lane="system"] .analyze-button--invite::after {
    animation: analyzeInviteFade 1.2s var(--bz-ease-out) 3;
  }
}

@keyframes analyzeInvite {
  0% { opacity: 0.9; transform: scale(1); }
  100% { opacity: 0; transform: scale(1.16); }
}

@keyframes analyzeInviteFade {
  0%, 100% { opacity: 0; }
  40% { opacity: 0.9; }
}

/* The code: a solid plate of what floats, legible on a light counter and a dark one alike. */
:root[data-bz-lane="system"] .order-id {
  top: calc(var(--bz-bar-height) + var(--bz-space-4));
  right: var(--bz-gutter);
  height: auto;
  min-width: 0;
  padding: var(--bz-space-4) var(--bz-space-7);
  border: 1px solid var(--bz-edge);
  border-radius: var(--bz-radius-lg);
  background: var(--bz-raised);
  color: var(--bz-ink);
  box-shadow: var(--bz-shadow-overlay);
  backdrop-filter: none;
  font-family: var(--bz-font);
  font-size: var(--bz-text-code);
  line-height: var(--bz-leading-code);
  font-weight: var(--bz-weight-strong);
}

:root[data-bz-lane="system"] .cooldown-ring__track {
  stroke: var(--bz-edge);
}

:root[data-bz-lane="system"] .cooldown-ring__progress {
  stroke: var(--bz-ink);
}

/* The phone: the shutter a square of 96 in the middle, and the two beside it as wide as their column (at
   least 96: 3 × 96 + 2 × 12 fits a 360 screen inside its gutters; 119 on a 390 iPhone), icon over label at
   16 — Analizar and «Foto y analizar» are the bigger targets they were before the system (Adrián,
   14-oct-2026: they looked smaller than production's). The shutter's icon at 32, as on the tablet: at 24
   it was a small mark in a big square. The plate drops to the 28 step. */
@media (max-width: 767px) {
  :root[data-bz-lane="system"] .order-info-wrapper {
    padding-bottom: var(--bz-gutter);
  }

  :root[data-bz-lane="system"] .back-button,
  :root[data-bz-lane="system"] .camera-button,
  :root[data-bz-lane="system"] .analyze-button {
    flex-direction: column;
    gap: 0;
    width: var(--bz-target-hero);
    height: var(--bz-target-hero);
    padding: 0 var(--bz-space-2);
    border-radius: var(--bz-radius-xl);
    font-size: var(--bz-text-md);
    line-height: var(--bz-leading-md);
  }

  :root[data-bz-lane="system"] .back-button,
  :root[data-bz-lane="system"] .analyze-button {
    justify-self: stretch;
    width: auto;
    min-width: var(--bz-target-hero);
  }

  :root[data-bz-lane="system"] .camera-button {
    padding: 0;
  }

  :root[data-bz-lane="system"] .camera-button .action-icon {
    font-size: var(--bz-icon-lg);
  }

  :root[data-bz-lane="system"] .analyze-button .photo-count {
    order: 0;
    min-width: var(--bz-space-6);
    height: var(--bz-space-6);
    font-size: var(--bz-text-sm);
    line-height: var(--bz-leading-sm);
  }

  :root[data-bz-lane="system"] .action-icon {
    font-size: var(--bz-icon-md);
  }

  /* Hanging from the top of the camera, as the ticket's tab (TicketComponent.vue, 14-oct-2026). */
  :root[data-bz-lane="system"] .order-id {
    top: var(--bz-bar-height);
    right: auto;
    padding: var(--bz-space-1) var(--bz-space-5);
    border-top: 0;
    border-radius: 0 0 var(--bz-radius-lg) var(--bz-radius-lg);
    font-size: var(--bz-text-3xl);
    line-height: var(--bz-leading-3xl);
  }
}
</style>